import Phaser from 'phaser';
import { SCENE_KEYS, HEADER_CARD_GAP } from '@/config/constants';
import { submitScore, getPlayerRank } from '@/utils/SupabaseClient';
import { AudioManager } from '@/utils/AudioManager';
import { transitionTo, fadeIn } from '@/utils/UIAnimations';
import { PostFXManager } from '@/utils/PostFXManager';
import { BackgroundManager } from '@/utils/BackgroundManager';
import { COLORS, COLORS_HEX, FONTS } from '@/ui/theme';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { createSubtleLink } from '@/ui/SubtleLink';
import { GamepadNav } from '@/utils/GamepadNav';

/**
 * Game Over Scene — chunky cartoon redesign.
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │                  HELMEPA                         │
 *   │              POSEIDON CHALLENGE                  │
 *   │  ╭──────────────────────────────────────────╮    │
 *   │  │            GAME OVER  (magenta)          │    │
 *   │  │  ──────────────────────────────────────  │    │
 *   │  │       FINAL SCORE 1,780  (yellow)        │    │
 *   │  │           Energy depleted                │    │
 *   │  │   [HIGH SCORES] [PLAY AGAIN] [MAIN MENU] │    │
 *   │  ╰──────────────────────────────────────────╯    │
 *   └──────────────────────────────────────────────────┘
 */
export class GameOverScene extends Phaser.Scene {
  private finalScore = 0;
  private gameOverReason = 'Time over';
  private stageReached = 1;
  private playerRank = -1;
  private nav?: GamepadNav;

  constructor() {
    super({ key: SCENE_KEYS.GAME_OVER });
  }

  init(data: { score: number; reason?: string; stage?: number }): void {
    this.finalScore = data.score || 0;
    this.gameOverReason = data.reason || 'Time over';
    this.stageReached = data.stage ?? 1;
    this.playerRank = -1;
  }

  create(): void {
    const { width, height } = this.cameras.main;
    const cx = width / 2;

    fadeIn(this, 400);
    PostFXManager.attach(this, 'gameOver');

    // ── Background — stage 1 video looped via BackgroundManager (dual
    //    crossfade). Falls back to gradient if the video isn't loaded.
    const bgManager = new BackgroundManager(this);
    bgManager.createBackground({
      type: 'video',
      video: 'world1 video',
      scaleMode: 'cover',
    });
    // Light scrim — same translucency as the modals so the scene reads
    // through without overpowering the foreground card.
    const scrim = this.add.graphics().setDepth(1);
    scrim.fillStyle(0x020b18, 0.40).fillRect(0, 0, width, height);

    // ── Audio sting + Poseidon stoic line
    const am = AudioManager.getInstance();
    const goSfx = this.cache.audio.exists('sfx_game_over') ? 'sfx_game_over' : 'sfx_heart_lost';
    am.playSFX(this, goSfx, { volume: 0.7 });

    // Submit + fetch rank in parallel; UI doesn't block on it.
    void this.submitScoreToLeaderboard();

    // ── HEADER (HELMEPA + smaller POSEIDON CHALLENGE) ─────────────────
    if (this.textures.exists('helmepa_logo')) {
      const h = this.add.image(cx, 88, 'helmepa_logo')
        .setOrigin(0.5).setDepth(10).setAlpha(0.95);
      h.setScale(162 / h.width);
    }
    let titleBottom = 268.6; // fallback if title texture is missing
    if (this.textures.exists('title_logo_v4')) {
      const t = this.add.image(cx, 200, 'title_logo_v4').setOrigin(0.5).setDepth(10);
      t.setScale(378 / t.width);
      titleBottom = t.y + t.displayHeight / 2;
    }

    // ── PANEL ──────────────────────────────────────────────────────────
    const pw = 1200;
    const ph = 720;
    const panelTop = Math.round(titleBottom + HEADER_CARD_GAP);
    const panelLeft = cx - pw / 2;
    const panelRadius = 32;

    const panel = this.add.graphics().setDepth(2);
    panel.fillStyle(COLORS.deepOcean, 0.55);
    panel.fillRoundedRect(panelLeft, panelTop, pw, ph, panelRadius);
    panel.lineStyle(2, COLORS.aqua, 0.70);
    panel.strokeRoundedRect(panelLeft, panelTop, pw, ph, panelRadius);

    // ── GAME OVER title (Skranji, magenta) ────────────────────────────
    const titleY = panelTop + 130;
    const titleText = this.add.text(cx, titleY, 'GAME OVER', {
      fontFamily: FONTS.fancyRegular,
      fontSize: '128px',
      color: '#ff3df7',
      stroke: '#27003a',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 6, color: '#000000', blur: 14, fill: true },
    }).setOrigin(0.5).setDepth(10);

    // Title smash-in for a bit of energy
    titleText.setScale(0.6);
    this.tweens.add({
      targets: titleText,
      scale: 1,
      duration: 320,
      ease: 'Back.easeOut',
      delay: 120,
    });

    // ── Divider line under title ──────────────────────────────────────
    const divY = panelTop + 240;
    const div = this.add.graphics().setDepth(10);
    div.lineStyle(2, COLORS.aqua, 0.45);
    div.lineBetween(panelLeft + 80, divY, panelLeft + pw - 80, divY);

    // ── FINAL SCORE (Skranji, yellow) ─────────────────────────────────
    const finalScoreText = `FINAL SCORE ${this.finalScore.toLocaleString()}`;
    this.add.text(cx, panelTop + 340, finalScoreText, {
      fontFamily: FONTS.fancyRegular,
      fontSize: '76px',
      color: '#ffe000',
      stroke: '#3a2a00',
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 4, color: '#000000', blur: 8, fill: true },
    }).setOrigin(0.5).setDepth(10);

    // ── Reason (CF Asty Std Black, white) ─────────────────────────────
    const reasonStr = this.formatReason(this.gameOverReason);
    this.add.text(cx, panelTop + 430, reasonStr, {
      fontFamily: FONTS.displayHeavy,
      fontSize: '30px',
      color: COLORS_HEX.textPrimary,
      stroke: COLORS_HEX.midnightDepth,
      strokeThickness: 1,
    }).setOrigin(0.5).setDepth(10);

    // ── BUTTON ROW — three PLAY-style rectangular chips on one row.
    //    Sizing matches the home-screen PLAY button (360 × 110, 56px),
    //    so primary + secondary share the same silhouette.
    const btnY = panelTop + ph - 110;
    const btnW = 360;
    const gap = 30;

    const playAgain = createPillButton(this, {
      x: cx,
      y: btnY,
      label: 'PLAY AGAIN',
      variant: 'primary',
      fontSize: 44,
      onClick: () => this.handlePlayAgain(),
    });
    playAgain.setDepth(11);

    const highScores = createPillButton(this, {
      x: cx - btnW - gap,
      y: btnY,
      label: 'HIGH SCORES',
      variant: 'secondary',
      fontSize: 44,
      onClick: () => this.handleHighScores(),
    });
    highScores.setDepth(11);

    const mainMenu = createPillButton(this, {
      x: cx + btnW + gap,
      y: btnY,
      label: 'MAIN MENU',
      variant: 'secondary',
      fontSize: 44,
      onClick: () => this.handleMainMenu(),
    });
    mainMenu.setDepth(11);

    // Bottom-right CREDITS link, same silhouette as the home-screen SETTINGS
    // label: subtle white text with hover bloom, no pill chrome. Created BEFORE
    // the nav wiring so the joystick / keyboard can focus it as the final stop.
    const creditsLink = createSubtleLink(this, {
      x: width - 130,
      y: 985,
      label: 'CREDITS',
      onClick: () => transitionTo(this, SCENE_KEYS.CREDITS),
    }).setDepth(12);

    // Focus halo — nav order left → right then the corner link:
    //   HIGH SCORES, PLAY AGAIN, MAIN MENU, CREDITS.
    // Nothing is pre-selected (focusIdx = -1): the player must deliberately move
    // onto a CTA with the arrow keys / D-pad / mouse before ENTER/SPACE can
    // activate it. This stops leftover gameplay button-mashing (e.g. holding the
    // shoot key while losing the boss fight) from instantly firing PLAY AGAIN.
    const buttons = [highScores, playAgain, mainMenu];
    const creditsIdx = buttons.length;    // CREDITS sits just past the pills
    const navCount = buttons.length + 1;  // pills + CREDITS link
    let focusIdx = -1; // -1 = no selection yet
    const halo = this.add.graphics().setDepth(12);
    const drawHalo = () => {
      if (focusIdx < 0) { halo.clear(); return; }
      // CREDITS is a text link, not a pill — give it the SAME bold focus ring
      // as the CTAs (drawn as a rounded rect) so a joystick / keyboard player
      // can clearly see the selection land on it. Without this the corner link
      // only lit its own faint glow, which reads as "focus disappeared".
      if (focusIdx === creditsIdx) {
        const sx = creditsLink.scaleX, sy = creditsLink.scaleY;
        drawSelectionHalo(halo, {
          cx: creditsLink.x,
          cy: creditsLink.y,
          w: 200 * sx,
          h: 60 * sy,
          rx: 30 * sx,
          ry: 30 * sy,
        }, { gap: 12 });
        return;
      }
      const b = buttons[focusIdx];
      // Offset the button's CURRENT (scaled) pill — using its real elliptical
      // cap radii — by an even gap, so the ring stays a parallel pill that grows
      // with the button on select.
      drawSelectionHalo(halo, {
        cx: b.x,
        cy: b.y,
        w: ((b as any).pillWidth ?? btnW) * b.scaleX,
        h: ((b as any).pillHeight ?? 110) * b.scaleY,
        rx: ((b as any).pillCapRx ?? 55) * b.scaleX,
        ry: ((b as any).pillCapRy ?? 55) * b.scaleY,
      }, { gap: 14 });
    };
    // Move focus and toggle the CREDITS link's built-in halo on enter / leave.
    // The mouse-hover sync listener below never emits, so there is no recursion.
    const setFocus = (next: number) => {
      const wasCredits = focusIdx === creditsIdx;
      const isCredits = next === creditsIdx;
      if (wasCredits && !isCredits) creditsLink.emit('pointerout');
      if (!wasCredits && isCredits) creditsLink.emit('pointerover');
      focusIdx = next;
      drawHalo();
    };
    drawHalo();
    // Redraw each frame so the ring tracks the button's grow-on-select tween.
    this.events.on(Phaser.Scenes.Events.UPDATE, drawHalo);
    buttons.forEach((b, i) => b.on('pointerover', () => setFocus(i)));
    // Mouse hover on CREDITS only syncs the focus index (its visible halo comes
    // from the SubtleLink's own pointerover handler) — no emit → no recursion.
    creditsLink.on('pointerover', () => { focusIdx = creditsIdx; drawHalo(); });

    // Shared step/confirm helpers. The first arrow press lands on PLAY AGAIN
    // (the primary) and steps from there; `confirm` is a no-op until something
    // is selected, so a held SPACE (the shoot key) can't fire a CTA.
    const step = (dir: number) => {
      const next = focusIdx < 0 ? 1 : Phaser.Math.Clamp(focusIdx + dir, 0, navCount - 1);
      setFocus(next);
    };
    const confirm = () => {
      if (focusIdx < 0) return;
      if (focusIdx === creditsIdx) { creditsLink.emit('pointerdown'); return; }
      (buttons[focusIdx] as any).press?.();
    };
    const fireCancel = () => (mainMenu as any).press?.();

    // Keyboard: ←/→ move the selection, ENTER/SPACE confirm the SELECTED CTA,
    // ESC always returns to the main menu.
    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-LEFT',  () => step(-1));
      kb.on('keydown-RIGHT', () => step(1));
      kb.on('keydown-ENTER', confirm);
      kb.on('keydown-SPACE', confirm);
      kb.on('keydown-ESC', fireCancel);
    }

    // Gamepad: D-pad/stick steps focus across the row (now including CREDITS),
    // A and B both press the focused CTA. No hard-wired shortcut from B, so you
    // cannot fire a CTA that does not have the focus frame on it. (Keyboard ESC
    // still cancels.)
    this.nav = new GamepadNav(this, {
      onLeft:  () => step(-1),
      onRight: () => step(1),
      onConfirm: confirm,
    });

    // Tear down both background videos when this scene shuts down.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => bgManager.destroyBackground());
  }

  update(): void {
    this.nav?.update();
  }

  /** First letter capitalized, rest lowercased — matches the "Energy depleted" style. */
  private formatReason(reason: string): string {
    if (!reason) return '';
    const trimmed = reason.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  }

  private async submitScoreToLeaderboard(): Promise<void> {
    const playerId = this.registry.get('playerId');
    const playerName = this.registry.get('playerName');
    if (!playerName || !playerId) return;
    try {
      await submitScore({
        playerId,
        playerName,
        stageNumber: this.stageReached,
        stageScore: this.finalScore,
        totalScore: this.finalScore,
        timeRemaining: 0,
        livesRemaining: 0,
        completed: false,
      });
      this.playerRank = await getPlayerRank(this.finalScore);
      console.log(`🏆 Player rank: #${this.playerRank}`);
    } catch (e) {
      console.warn('Score submit failed:', e);
    }
  }

  private handlePlayAgain(): void {
    const hasIdentity = !!this.registry.get('playerName');
    if (hasIdentity) {
      transitionTo(this, SCENE_KEYS.GAME, { stage: 1, score: 0 }, 500, 0x000000, 'parallaxPush');
    } else {
      transitionTo(this, SCENE_KEYS.NAME_ENTRY, { stage: 1 });
    }
  }
  private handleMainMenu(): void {
    transitionTo(this, SCENE_KEYS.MENU);
  }
  private handleHighScores(): void {
    transitionTo(this, SCENE_KEYS.LEADERBOARD, { highlightScore: this.finalScore });
  }
}
